import cn from 'classnames';
import { useEffect, useState, type ReactElement } from 'react';
import { Tooltip } from '@mui/material';
import { Search } from '@features/common';
import { Add, Search as SearchIcon } from '@shared/assets';
import { iconsMap } from '@shared/assets/icons/map.tsx';
import { usePlatformSettings, useWindowWidth } from '@shared/hooks';
import type { Client } from '@shared/types/users';
import { Tabs as CommonTabs, Tab } from '@shared/ui';
import { TabsItemIcon } from '@shared/ui/tabs/tab-icon';
import { useTabs } from '../..';
import * as st from './styles.module.scss';

interface Props {
  client: Client | undefined;
  searchNotes: string;
  onSearchNotes: (searchNotes: string) => void;
  Profile: (id: number) => ReactElement;
  Notes: (id: number, searchNotes: string) => ReactElement;
  RentedPrograms: (id: number, code: string) => ReactElement;
  RentedArticles: (id: number) => ReactElement;
  AssignedConsultations: (id: number, code: string) => ReactElement;
  EventsList: (id: number, code: string) => ReactElement;
  GroupsList: (id: number) => ReactElement;
  ColorsList: (id: number) => ReactElement;
  ContactsList: (id: number) => ReactElement;
}

export const Tabs: React.FC<Props> = ({
  client,
  ContactsList,
  searchNotes,
  onSearchNotes,
  Notes,
  RentedPrograms,
  RentedArticles,
  AssignedConsultations,
  EventsList,
  Profile,
  GroupsList,
  ColorsList,
}) => {
  const {
    selectedModule,
    selectedTab,
    onTabChange,
    clientModules,
    toggleActionModal,
    selectedClient,
  } = useTabs(client?.id);
  const windowWidth = useWindowWidth();

  // const { searchNotes, onSearchNotes } = useFilter();

  const [isSearch, setIsSearch] = useState(false);

  // const hasLibrary: AppModule | undefined = clientModules.find(
  //   (mod) => mod.module_type === selectedTab
  // );
  const { has_library } = usePlatformSettings();
  const typesAddButton = ['programs', 'events', 'schedule', 'notes', 'groups', 'contacts'];

  const tabTooltipItem = (
    module:
      | 'profile'
      | 'contacts'
      | 'programs'
      | 'events'
      | 'schedule'
      | 'notes'
      | 'groups'
      | 'colorTags'
      | string,
    library?: boolean
  ) => {
    switch (module) {
      case 'profile':
        return 'Данные';
      case 'contacts':
        return 'Контакты';
      case 'notes':
        return 'Заметки';
      case 'groups':
        return 'Группы';
      case 'colorTags':
        return 'Теги';
    }
  };

  const tabTooltipAdd = (
    tabName: 'programs' | 'events' | 'schedule' | 'notes' | 'groups' | string,
    library?: boolean
  ) => {
    const add = () => {
      switch (tabName) {
        case 'contacts':
          return 'Добавить контакт';
        case 'notes':
          return 'Добавить заметку';
        case 'schedule':
          return 'Добавить встречу';
        // case 'events':
        //   if (!library) return 'Добавить мероприятие';
        // case 'programs':
        //   return 'Добавить видео';
        case 'groups':
          return 'Добавить группу';

        default:
          return;
      }
    };
    if (add())
      return (
        <Tooltip title={add()}>
          <div>
            <Tab isActive onClick={toggleActionModal}>
              <Add />
            </Tab>
          </div>
        </Tooltip>
      );
  };
  // console.log('clientModules: ', clientModules);
  useEffect(() => {}, [searchNotes]);

  const onCloseSearchNote = () => setIsSearch(!isSearch);
  return (
    <>
      {selectedClient && (
        <div className={st.tabs}>
          <div className={st.tabs__buttons}>
            {!isSearch && (
              <CommonTabs>
                {!isSearch && (
                  <Tooltip title={tabTooltipItem('profile', true)}>
                    <div>
                      <Tab
                        isActive={selectedTab === 'profile'}
                        onClick={() => onTabChange('profile')}
                      >
                        <TabsItemIcon hasLibrary={true} moduleType="profile" alt="profile" />
                      </Tab>
                    </div>
                  </Tooltip>
                )}
                {!isSearch && (
                  <Tooltip title={tabTooltipItem('contacts', true)}>
                    <div>
                      <Tab
                        isActive={selectedTab === 'contacts'}
                        onClick={() => onTabChange('contacts')}
                      >
                        <TabsItemIcon hasLibrary={true} moduleType="contacts" alt="contacts" />
                      </Tab>
                    </div>
                  </Tooltip>
                )}

                {!isSearch && (
                  <Tooltip title={tabTooltipItem('notes', true)}>
                    <div>
                      <Tab isActive={selectedTab === 'notes'} onClick={() => onTabChange('notes')}>
                        <TabsItemIcon hasLibrary={true} moduleType="notes" alt="notes" />
                      </Tab>
                    </div>
                  </Tooltip>
                )}

                {clientModules.map(
                  (mod) =>
                    !isSearch && (
                      <Tooltip title={mod.verbose_name} key={mod.id}>
                        <div>
                          <Tab
                            isActive={selectedModule?.code === mod.code}
                            onClick={() => onTabChange(mod.module_type, mod)}
                            className={cn(st.tab, st[mod.module_type])}
                          >
                            {iconsMap[mod.module_type]}
                          </Tab>
                        </div>
                      </Tooltip>
                    )
                )}
                {!isSearch && (
                  <Tooltip title={tabTooltipItem('groups', true)}>
                    <div>
                      <Tab
                        isActive={selectedTab === 'groups'}
                        onClick={() => onTabChange('groups')}
                      >
                        <TabsItemIcon hasLibrary={true} moduleType="groups" alt="groups" />
                      </Tab>
                    </div>
                  </Tooltip>
                )}
                {!isSearch && (
                  <Tooltip title={tabTooltipItem('colorTags', true)}>
                    <div>
                      <Tab
                        isActive={selectedTab === 'colorTags'}
                        onClick={() => onTabChange('colorTags')}
                      >
                        <TabsItemIcon hasLibrary={true} moduleType="colorTags" alt="colorTags" />
                      </Tab>
                    </div>
                  </Tooltip>
                )}
              </CommonTabs>
            )}

            <div className={cn(st.tabs__actionButton, isSearch && st.tabs__searchActive)}>
              {selectedTab === 'notes' &&
                !isSearch &&
                (windowWidth >= 1050 ? (
                  <Search search={searchNotes} onSearch={onSearchNotes} whereUse="notes" />
                ) : (
                  <Tab isActive needHover onClick={() => setIsSearch(!isSearch)}>
                    <SearchIcon />
                  </Tab>
                ))}

              {isSearch && (
                <Search
                  mobile
                  search={searchNotes}
                  onSearch={onSearchNotes}
                  whereUse="notes"
                  onClose={onCloseSearchNote}
                />
              )}

              {typesAddButton.includes(selectedTab) && tabTooltipAdd(selectedTab, has_library)}
            </div>
          </div>
          <div className={st.tabs__tab}>
            {selectedTab === 'profile' && selectedClient && Profile(selectedClient?.id)}

            {selectedTab === 'contacts' && selectedClient && ContactsList(selectedClient?.id)}

            {selectedTab === 'notes' && selectedClient && Notes(selectedClient?.id, searchNotes)}

            {selectedTab === 'programs' &&
              selectedClient &&
              selectedModule &&
              RentedPrograms(selectedClient?.id, selectedModule.code)}

            {selectedTab === 'articles' &&
              selectedClient &&
              selectedModule &&
              RentedArticles(selectedClient?.id)}

            {selectedTab === 'schedule' &&
              selectedClient &&
              selectedModule &&
              AssignedConsultations(selectedClient?.id, selectedModule.code)}

            {selectedTab === 'events' &&
              selectedClient &&
              selectedModule &&
              EventsList(selectedClient?.id, selectedModule.code)}

            {selectedTab === 'groups' && selectedClient && GroupsList(selectedClient?.id)}
            {selectedTab === 'colorTags' && selectedClient && ColorsList(selectedClient?.id)}
          </div>
        </div>
      )}
    </>
  );
};
